// ============================================================
// 用户头像组件 — 支持预设 SVG 和图片 URL
// ============================================================

import { Avatar } from 'antd'
import { UserOutlined } from '@ant-design/icons'

// ─── 预设头像颜色 ───────────────────────────────────────────

const AVATAR_COLORS: Record<string, { bg: string; fg: string }> = {
  'avatar-01': { bg: '#8B5CF6', fg: '#FFFFFF' },
  'avatar-02': { bg: '#EC4899', fg: '#FFFFFF' },
  'avatar-03': { bg: '#3B82F6', fg: '#FFFFFF' },
  'avatar-04': { bg: '#10B981', fg: '#FFFFFF' },
  'avatar-05': { bg: '#F59E0B', fg: '#FFFFFF' },
  'avatar-06': { bg: '#EF4444', fg: '#FFFFFF' },
  'avatar-07': { bg: '#6366F1', fg: '#FFFFFF' },
  'avatar-08': { bg: '#14B8A6', fg: '#FFFFFF' },
  'avatar-09': { bg: '#F97316', fg: '#FFFFFF' },
  'avatar-10': { bg: '#8B5CF6', fg: '#EC4899' },
}

// ─── 预设头像 SVG ───────────────────────────────────────────

function AvatarSVG({ name, size = 80 }: { name: string; size?: number }) {
  const colors = AVATAR_COLORS[name] || AVATAR_COLORS['avatar-01']
  const idx = parseInt(name.split('-')[1]) - 1

  const shapes = [
    // 01: 圆形脸 + 眼睛
    <g key="0"><circle cx="50" cy="50" r="40" fill={colors.fg} opacity="0.3"/><circle cx="38" cy="42" r="4" fill={colors.fg}/><circle cx="62" cy="42" r="4" fill={colors.fg}/><path d="M38 60 Q50 70 62 60" stroke={colors.fg} strokeWidth="3" fill="none"/></g>,
    // 02: 方形脸
    <g key="1"><rect x="20" y="20" width="60" height="60" rx="12" fill={colors.fg} opacity="0.3"/><circle cx="38" cy="42" r="4" fill={colors.fg}/><circle cx="62" cy="42" r="4" fill={colors.fg}/><rect x="38" y="58" width="24" height="4" rx="2" fill={colors.fg}/></g>,
    // 03: 猫耳
    <g key="2"><circle cx="50" cy="52" r="36" fill={colors.fg} opacity="0.3"/><polygon points="22,28 30,10 40,28" fill={colors.fg} opacity="0.5"/><polygon points="60,28 70,10 78,28" fill={colors.fg} opacity="0.5"/><circle cx="38" cy="48" r="3" fill={colors.fg}/><circle cx="62" cy="48" r="3" fill={colors.fg}/><path d="M42 62 Q50 68 58 62" stroke={colors.fg} strokeWidth="2" fill="none"/></g>,
    // 04: 星星眼
    <g key="3"><circle cx="50" cy="50" r="38" fill={colors.fg} opacity="0.3"/><polygon points="38,38 40,44 46,44 41,48 43,54 38,50 33,54 35,48 30,44 36,44" fill={colors.fg}/><polygon points="62,38 64,44 70,44 65,48 67,54 62,50 57,54 59,48 54,44 60,44" fill={colors.fg}/><path d="M40 66 Q50 74 60 66" stroke={colors.fg} strokeWidth="2" fill="none"/></g>,
    // 05: 墨镜
    <g key="4"><circle cx="50" cy="50" r="38" fill={colors.fg} opacity="0.3"/><rect x="26" y="38" width="18" height="12" rx="4" fill={colors.fg}/><rect x="56" y="38" width="18" height="12" rx="4" fill={colors.fg}/><line x1="44" y1="44" x2="56" y2="44" stroke={colors.fg} strokeWidth="2"/><path d="M38 66 Q50 74 62 66" stroke={colors.fg} strokeWidth="2" fill="none"/></g>,
    // 06: 爱心嘴
    <g key="5"><circle cx="50" cy="50" r="38" fill={colors.fg} opacity="0.3"/><circle cx="38" cy="42" r="4" fill={colors.fg}/><circle cx="62" cy="42" r="4" fill={colors.fg}/><path d="M44 60 Q44 56 47 56 Q50 56 50 60 Q50 56 53 56 Q56 56 56 60 Q56 66 50 70 Q44 66 44 60Z" fill={colors.fg}/></g>,
    // 07: 皇冠
    <g key="6"><polygon points="30,30 36,18 42,26 50,14 58,26 64,18 70,30" fill={colors.fg} opacity="0.6"/><circle cx="50" cy="52" r="32" fill={colors.fg} opacity="0.3"/><circle cx="40" cy="48" r="3" fill={colors.fg}/><circle cx="60" cy="48" r="3" fill={colors.fg}/><path d="M42 62 Q50 68 58 62" stroke={colors.fg} strokeWidth="2" fill="none"/></g>,
    // 08: 闪电
    <g key="7"><polygon points="52,10 40,40 50,40 42,70 62,32 52,32 58,10" fill={colors.fg} opacity="0.5"/><circle cx="50" cy="50" r="38" fill={colors.fg} opacity="0.2"/><circle cx="40" cy="46" r="3" fill={colors.fg}/><circle cx="60" cy="46" r="3" fill={colors.fg}/></g>,
    // 09: 王冠
    <g key="8"><rect x="28" y="14" width="44" height="8" rx="2" fill={colors.fg} opacity="0.5"/><rect x="32" y="22" width="4" height="10" fill={colors.fg} opacity="0.5"/><rect x="48" y="22" width="4" height="10" fill={colors.fg} opacity="0.5"/><rect x="64" y="22" width="4" height="10" fill={colors.fg} opacity="0.5"/><circle cx="50" cy="56" r="30" fill={colors.fg} opacity="0.3"/><circle cx="40" cy="52" r="3" fill={colors.fg}/><circle cx="60" cy="52" r="3" fill={colors.fg}/></g>,
    // 10: 机器人
    <g key="9"><rect x="24" y="24" width="52" height="44" rx="8" fill={colors.fg} opacity="0.3"/><rect x="42" y="14" width="16" height="10" rx="4" fill={colors.fg} opacity="0.4"/><rect x="32" y="36" width="12" height="10" rx="3" fill={colors.fg}/><rect x="56" y="36" width="12" height="10" rx="3" fill={colors.fg}/><rect x="40" y="54" width="20" height="6" rx="3" fill={colors.fg}/></g>,
  ]

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ borderRadius: '50%' }}>
      <rect width="100" height="100" fill={colors.bg} rx="50" />
      {shapes[idx] || shapes[0]}
    </svg>
  )
}

// ─── 判断是否为预设头像 ─────────────────────────────────────

function isPresetAvatar(avatar: string | undefined | null): boolean {
  return !!avatar && avatar.startsWith('avatar-')
}

// ─── 主组件 ─────────────────────────────────────────────────

interface UserAvatarProps {
  avatar?: string | null
  size?: number
  style?: React.CSSProperties
}

export default function UserAvatar({ avatar, size = 40, style }: UserAvatarProps) {
  if (isPresetAvatar(avatar)) {
    return (
      <Avatar size={size} style={{ background: 'transparent', ...style }}>
        <AvatarSVG name={avatar!} size={size - 4} />
      </Avatar>
    )
  }

  return (
    <Avatar
      size={size}
      src={avatar || undefined}
      icon={<UserOutlined />}
      style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', ...style }}
    />
  )
}
